import { useEffect, useMemo, useRef, useState } from 'react';
import {
  usePaginatedSpecializations,
  useCreateSpecialization,
  useUpdateSpecialization,
  useDeleteSpecialization,
  useBulkDeleteSpecializations,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { toast } from 'sonner';
import {
  Stethoscope,
  Plus,
  Edit2,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  Search,
  KeyRound,
  AlignLeft,
  CheckCircle,
  XCircle,
} from 'lucide-react';

export function Specializations() {
  const limit = 20;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [viewing, setViewing] = useState<any>(null);
  const [form, setForm] = useState({
    name: '',
    label: '',
    description: '',
    isActive: true,
  });

  const { data: specializationsData, isLoading, error } = usePaginatedSpecializations(page, limit, debouncedSearch || undefined);
  const createSpec = useCreateSpecialization();
  const updateSpec = useUpdateSpecialization();
  const deleteSpec = useDeleteSpecialization();
  const bulkDeleteSpec = useBulkDeleteSpecializations();

  const specializations = useMemo(() => specializationsData?.data || [], [specializationsData]);
  const meta = specializationsData?.meta;

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(specializations.map((s: any) => s.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [specializations]);

  const allSelected = specializations.length > 0 && specializations.every((s: any) => selectedIds.has(s.id));
  const someSelected = specializations.some((s: any) => selectedIds.has(s.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = specializations.length > 0 && specializations.every((s: any) => prev.has(s.id));
      return everySelected ? new Set() : new Set(specializations.map((s: any) => s.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      await bulkDeleteSpec.mutateAsync(ids);
      setSelectedIds(new Set());
      toast.success(`Deleted ${ids.length} specialization${ids.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to delete some specializations');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', label: '', description: '', isActive: true });
    setShowForm(false);
    setEditing(null);
  };

  const startEdit = (s: any) => {
    setEditing(s);
    setForm({
      name: s.name,
      label: s.label,
      description: s.description || '',
      isActive: s.isActive,
    });
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { ...form, name: form.name || form.label.toLowerCase().replace(/[^a-z0-9]+/g, '_') };
    if (editing) {
      updateSpec.mutate({ id: editing.id, data: payload }, { onSuccess: resetForm });
    } else {
      createSpec.mutate(payload, { onSuccess: resetForm });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSpec.mutateAsync(deleteTarget.id);
      toast.success('Specialization deleted');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete specialization');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Stethoscope className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-text">Specializations</h1>
            <p className="text-sm text-text-muted">Manage doctor specializations used across the system.</p>
          </div>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          Add Specialization
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load specializations</span>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search specializations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => { setSearch(''); setPage(1); }}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteSpec.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteSpec.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteSpec.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-background border-b border-border">
                <tr>
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={specializations.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Label</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Key</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {specializations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                      {search ? (
                        <span>
                          No specializations match your search.{' '}
                          <button onClick={() => { setSearch(''); setPage(1); }} className="text-primary hover:underline">
                            Clear search
                          </button>
                        </span>
                      ) : (
                        'No specializations configured. Add your first specialization to get started.'
                      )}
                    </td>
                  </tr>
                ) : (
                  specializations.map((spec: any) => (
                    <tr key={spec.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(spec.id)}
                          onChange={() => toggleOne(spec.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Stethoscope size={14} className="text-primary" />
                          </div>
                          <button
                            onClick={() => setViewing(spec)}
                            className="text-sm font-medium text-text hover:text-primary transition-colors text-left cursor-pointer"
                          >
                            {spec.label}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted font-mono">{spec.name}</td>
                      <td className="px-4 py-3 text-sm text-text-muted max-w-xs truncate">{spec.description || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${spec.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                          {spec.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => startEdit(spec)} className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-primary transition-colors" title="Edit">
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget({ id: spec.id, label: spec.label })}
                            className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-danger transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="specializations"
          />
        </div>
      )}

      {/* Details Modal */}
      {viewing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setViewing(null)}
        >
          <div
            className="bg-surface rounded-xl border border-border shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <Stethoscope size={16} className="text-primary" />
                </div>
                <h2 className="text-lg font-semibold text-text">{viewing.label}</h2>
              </div>
              <button onClick={() => setViewing(null)} className="p-1 hover:bg-background rounded-lg transition-colors">
                <X size={18} className="text-text-muted" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-start gap-3 bg-background rounded-lg p-3 border border-border">
                  <div className="p-2 bg-primary/10 rounded-lg"><KeyRound size={15} className="text-primary" /></div>
                  <div>
                    <p className="text-xs text-text-muted">Key</p>
                    <p className="text-sm font-medium text-text font-mono">{viewing.name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-background rounded-lg p-3 border border-border">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    {viewing.isActive ? <CheckCircle size={15} className="text-primary" /> : <XCircle size={15} className="text-primary" />}
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">Status</p>
                    <p className={`text-sm font-medium ${viewing.isActive ? 'text-success' : 'text-danger'}`}>
                      {viewing.isActive ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-background rounded-lg p-3 border border-border">
                <div className="p-2 bg-primary/10 rounded-lg"><AlignLeft size={15} className="text-primary" /></div>
                <div className="min-w-0">
                  <p className="text-xs text-text-muted">Description</p>
                  <p className="text-sm text-text whitespace-pre-wrap break-words">{viewing.description || '—'}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-4 py-4 border-t border-border">
              <button
                type="button"
                onClick={() => setViewing(null)}
                className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => { const s = viewing; setViewing(null); startEdit(s); }}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Edit2 size={16} />
                Edit Specialization
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-text">{editing ? 'Edit Specialization' : 'New Specialization'}</h2>
              <button onClick={resetForm} className="p-1 hover:bg-background rounded-lg transition-colors">
                <X size={18} className="text-text-muted" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Label *</label>
                <input
                  required
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="e.g. Cardiology"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Key (auto-generated if empty)</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. cardiology"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional description"
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="specIsActive" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                <label htmlFor="specIsActive" className="text-sm text-text">Active</label>
              </div>
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <button type="button" onClick={resetForm} className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors">Cancel</button>
                <button type="submit" disabled={createSpec.isPending || updateSpec.isPending} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                  {(createSpec.isPending || updateSpec.isPending) && <Loader2 size={16} className="animate-spin" />}
                  {editing ? 'Update Specialization' : 'Create Specialization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete specialization?"
        description={
          <>
            This will delete <strong className="text-text">{deleteTarget?.label}</strong>. This
            cannot be undone.
          </>
        }
        confirmLabel="Delete specialization"
        loading={deleteSpec.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} specialization${selectedIds.size === 1 ? '' : 's'}?`}
        description="This action will remove the selected specializations. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size} specialization${selectedIds.size === 1 ? '' : 's'}`}
        loading={bulkDeleteSpec.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
